import {
  LANE_IDS,
  type CurrentCoreAlphaGameState,
  type GameEvent,
  type SupplySnapshot,
} from "../types";
import { getSupplySnapshot } from "../selectors/supply";

export interface SupplyTransitionResult {
  readonly state: CurrentCoreAlphaGameState;
  readonly events: readonly GameEvent[];
}

function eventId(actionSequence: number, ordinal: number): string {
  return `event-${actionSequence}-${ordinal}`;
}

export function finalizeSupply(
  state: Readonly<CurrentCoreAlphaGameState>,
  snapshot: Readonly<SupplySnapshot>,
  actionSequence: number,
  startingEventOrdinal: number,
): SupplyTransitionResult {
  if (snapshot.unsustainableLaneIds.length > 0) {
    throw new Error("Supply cannot finalize while a lane remains unsustainable.");
  }
  if (state.supplyStatus.type === "resolved") {
    throw new Error("Supply cannot finalize twice for the same turn.");
  }

  const events: GameEvent[] = [];
  for (const laneId of LANE_IDS) {
    const lane = snapshot.lanes[laneId];
    if (!lane.active) continue;
    events.push({
      type: "local-upkeep-paid",
      eventId: eventId(actionSequence, startingEventOrdinal + events.length),
      actionSequence,
      playerId: state.activePlayerId,
      laneId,
      production: lane.production,
      upkeep: lane.upkeep,
      remainder: lane.remainder,
    });
  }

  events.push({
    type: "kingdom-pool-increased",
    eventId: eventId(actionSequence, startingEventOrdinal + events.length),
    actionSequence,
    playerId: state.activePlayerId,
    contribution: snapshot.kingdomPoolContribution,
    kingdomPool: snapshot.kingdomPoolContribution,
  });
  events.push({
    type: "supply-resolved",
    eventId: eventId(actionSequence, startingEventOrdinal + events.length),
    actionSequence,
    playerId: state.activePlayerId,
    turnNumber: state.turnNumber,
    kingdomPool: snapshot.kingdomPoolContribution,
  });

  return {
    state: {
      ...state,
      players: {
        ...state.players,
        [state.activePlayerId]: {
          ...state.players[state.activePlayerId],
          kingdomPool: snapshot.kingdomPoolContribution,
        },
      },
      supplyStatus: {
        type: "resolved",
        playerId: state.activePlayerId,
        turnNumber: state.turnNumber,
      },
    },
    events,
  };
}

export function resolveSupplyEntry(
  state: Readonly<CurrentCoreAlphaGameState>,
  actionSequence: number,
  startingEventOrdinal: number,
): SupplyTransitionResult {
  const snapshot = getSupplySnapshot(state);
  const events: GameEvent[] = [
    {
      type: "supply-started",
      eventId: eventId(actionSequence, startingEventOrdinal),
      actionSequence,
      playerId: state.activePlayerId,
      turnNumber: state.turnNumber,
    },
  ];

  for (const laneId of LANE_IDS) {
    const lane = snapshot.lanes[laneId];
    if (!lane.active || lane.landTypeId === null || lane.landStage === null) continue;
    events.push({
      type: "land-production-calculated",
      eventId: eventId(actionSequence, startingEventOrdinal + events.length),
      actionSequence,
      playerId: state.activePlayerId,
      laneId,
      landTypeId: lane.landTypeId,
      landStage: lane.landStage,
      production: lane.production,
      upkeep: lane.upkeep,
      shortfall: lane.shortfall,
      remainder: lane.remainder,
      armyCardInstanceIds: lane.armyCardInstanceIds,
    });
  }

  if (snapshot.unsustainableLaneIds.length === 0) {
    const finalized = finalizeSupply(
      state,
      snapshot,
      actionSequence,
      startingEventOrdinal + events.length,
    );
    return { state: finalized.state, events: [...events, ...finalized.events] };
  }

  for (const laneId of snapshot.unsustainableLaneIds) {
    const lane = snapshot.lanes[laneId];
    events.push({
      type: "upkeep-shortfall-detected",
      eventId: eventId(actionSequence, startingEventOrdinal + events.length),
      actionSequence,
      playerId: state.activePlayerId,
      laneId,
      shortfall: lane.shortfall,
      armyCardInstanceIds: lane.armyCardInstanceIds,
    });
  }

  return {
    state: {
      ...state,
      supplyStatus: {
        type: "awaiting-upkeep-discard",
        playerId: state.activePlayerId,
        turnNumber: state.turnNumber,
      },
    },
    events,
  };
}
